import xhrService from "../xhrService";
import { Address } from "./address";
import { Client, Contact } from "./client";
const baseUrl = "/api/projects";

/**
 * Finish types
 *
 */
export type Project = {
  id: number;
  name: string;
  client_id: number;
  address_id: number;
  inspection_template_id: number;
  permittee?: string;
  notes?: string;
  timezone: string;
  document_group: number;
  start_date: string;
  end_date: string;
  inspection_compliance_required: boolean;
  cgp_tracking_id?: number;
  next_inspection_date: string;
  archivedAt?: string;
  findingComplianceRequired: boolean;
  isCcrProject: boolean;
  address?: Address;
  client?: Client;
  inspector_user_id?: number;
  createdAt: Date;
};

/**
 * Go through types
 */
type ProjectApi = {
  create: (data: any) => Promise<Project>;
  get: (id: number) => Promise<Project>;
  getDocs: (id: number, params?: any) => Promise<any>;
  getLog: (id: number) => Promise<any>;
  update: (id: number, data: any) => Promise<Project>;
  setInspectionDate: (id: number, data: { created_date: Date }) => Promise<any>;
  bulkStorm: {
    get: (projectIds: Array<number>) => Promise<any>;
    create: (data: any) => Promise<any>;
  };
  contacts: {
    index: (projectId: number) => Promise<Array<Contact>>;
    create: (projectId: number, userIds: Array<number>) => Promise<Contact>;
    destroy: (projectId: number, userId: number) => Promise<any>;
  };
  removeDuplicateLayers: (id: number) => Promise<any>;
  getProjectLegendItemLog: (id: number, legendItemId: number, showFull: boolean) => Promise<any>;
  getNotes: (id: number, showFull: boolean) => Promise<any>
};

export const projectApi: ProjectApi = {
  create: data => {
    return xhrService.post(baseUrl, data).then(response => response.data);
  },
  get: (id: number) => {
    return xhrService.get(`${baseUrl}/${id}`).then(response => response.data);
  },
  /**
   * @return {Promise<{docTypes: any[], regulations: any[], document_group_name: string}>}
   */
  getDocs: (id: number, doctype?: any) => {
    const params: {doctype?: any} = {}

    if(doctype){
      params.doctype = doctype
    }

    return xhrService
      .get(`${baseUrl}/${id}/docs`, {params})
      .then(response => response.data);
  },
  getLog: (id: number) => {
    return xhrService
      .get(`/api/changelog/projects/frontendChangelogs/${id}`)
      .then(response => response.data);
  },
  update: (id: number, data) => {
    return xhrService
      .put(`${baseUrl}/${id}`, data)
      .then(response => response.data);
  },
  /**
   * Change the due date of the next inspection
   *
   * @param {number} id Primary ket of project
   * @param {{ created_date: string }} data Form data
   */
  setInspectionDate: (id: number, data: { created_date: Date }) => {
    return xhrService
      .put(`${baseUrl}/${id}/attr/next_inspection_date`, {
        value: data.created_date,
      })
      .then(response => response.data)
      .catch(error => Promise.reject(error.response.data));
  },
  bulkStorm: {
    get: (projectIds: Array<number>) => {
      return xhrService
        .get("/api/projects/post-storm", { params: { projectIds } })
        .then(res => res.data);
    },
    create: data => {
      return xhrService
        .post("/api/projects/post-storm", { data })
        .then(res => res.data);
    },
  },

  contacts: {
    index: (projectId: number) =>
      xhrService.get(`${baseUrl}/${projectId}/contacts`).then(res => res.data),
    create: (projectId: number, userIds: Array<number>) =>
      xhrService
        .post(`${baseUrl}/${projectId}/contacts`, { userIds })
        .then(res => res.data),
    destroy: (projectId: number, userId: number) =>
      xhrService
        .delete(`${baseUrl}/${projectId}/contacts/${userId}`)
        .then(res => res.data),
  },

  removeDuplicateLayers: (id: number) => {
    return xhrService.post(`${baseUrl}/${id}/removeDuplicateLayers`).then(response => response.data);
  },

  getProjectLegendItemLog: (id: number, legendItemId: number, showFull: boolean) => {
    return xhrService
      .get(`/api/changelog/projects/${id}/legendItem/${legendItemId}`,{
        params: {
          showFull: showFull
        }
      })
      .then(response => response.data);
  },

  getNotes: (id: number, showFull: boolean) => {
    return xhrService
      .get(`/api/changelog/projects/${id}/getNotes`,{
        params: {
          showFull: showFull
        }
      })
      .then(response => response.data);
  }
};

export default projectApi;
